import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from "chart.js";
import { FCard } from "../../../components/card";
import TemperatureIcon from "../../../components/icons/TemperatureIcon";
import { centered } from "@ui/components/style/style";
import { buildTemperatureChartSeries, } from "./temperatureChartSeries";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
const MAX_X_LABELS = 12;
const thinLabels = (labels, maxLabels) => {
    if (labels.length <= maxLabels)
        return labels;
    const step = Math.ceil(labels.length / maxLabels);
    return labels.map((label, index) => index === 0 || index === labels.length - 1 || index % step === 0
        ? label
        : "");
};
const formatFatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h > 0 && m > 0)
        return `${h}h ${m}m`;
    if (h > 0)
        return `${h}h`;
    return `${m}m`;
};
export const TemperatureChart = ({ logs, title = "Temperature", }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const containerRef = useRef(null);
    const [chartWidthPx, setChartWidthPx] = useState(800);
    const measure = useCallback(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const w = el.getBoundingClientRect().width;
        if (w > 0)
            setChartWidthPx(Math.floor(w));
    }, []);
    useEffect(() => {
        measure();
        const ro = new ResizeObserver(() => measure());
        const el = containerRef.current;
        if (el)
            ro.observe(el);
        return () => ro.disconnect();
    }, [measure]);
    const processedData = useMemo(() => buildTemperatureChartSeries(logs, chartWidthPx, isMobile), [logs, chartWidthPx, isMobile]);
    const isAggregatedView = useMemo(() => processedData.some((d) => d.isAggregated), [processedData]);
    const allValues = useMemo(() => {
        const vals = [];
        for (const d of processedData) {
            if (d.temperature != null && !Number.isNaN(Number(d.temperature))) {
                vals.push(Number(d.temperature));
            }
            if (d.minTemperature != null && !Number.isNaN(Number(d.minTemperature))) {
                vals.push(Number(d.minTemperature));
            }
            if (d.maxTemperature != null && !Number.isNaN(Number(d.maxTemperature))) {
                vals.push(Number(d.maxTemperature));
            }
        }
        return vals;
    }, [processedData]);
    const hasTemperatureData = allValues.length > 0;
    const rawMin = hasTemperatureData ? Math.min(...allValues) : 0;
    const rawMax = hasTemperatureData ? Math.max(...allValues) : 1;
    const dataRange = rawMax - rawMin;
    const rangePad = Math.max(0.5, Math.min(dataRange * 0.05, 2));
    const roundDownToTenth = (value) => Math.floor(value * 10) / 10;
    const roundUpToTenth = (value) => Math.ceil(value * 10) / 10;
    const yMin = hasTemperatureData ? roundDownToTenth(rawMin - rangePad) : 0;
    const yMax = hasTemperatureData ? roundUpToTenth(rawMax + rangePad) : 1;
    const normalizedYMin = yMin === yMax ? yMin - 0.5 : yMin;
    const normalizedYMax = yMin === yMax ? yMax + 0.5 : yMax;
    const ySpan = normalizedYMax - normalizedYMin;
    const getStepSize = (span) => {
        if (span <= 2)
            return 0.5;
        if (span <= 5)
            return 1;
        if (span <= 10)
            return 2;
        return Math.max(Math.ceil(span / 5), 1);
    };
    const stepSize = getStepSize(ySpan);
    const maxXLabels = isMobile ? 6 : MAX_X_LABELS;
    const chartLabels = useMemo(() => thinLabels(processedData.map((item) => item.month), maxXLabels), [processedData, maxXLabels]);
    const fatData = useMemo(() => processedData.map((d) => d.fatDuration != null
        ? Math.round((d.fatDuration / 60) * 100) / 100
        : null), [processedData]);
    const hasFatData = fatData.some((v) => v != null);
    const chartData = {
        labels: chartLabels,
        datasets: [
            {
                label: "Temperature (°C)",
                data: processedData.map((item) => item.temperature),
                borderColor: "#1D95E2",
                backgroundColor: "rgba(29, 149, 226, 0.06)",
                borderWidth: 2,
                spanGaps: true,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHitRadius: 14,
                pointHoverBackgroundColor: "#1D95E2",
                pointHoverBorderColor: "#FFFFFF",
                pointHoverBorderWidth: 2,
                tension: 0,
                fill: false,
                yAxisID: "y",
            },
            ...(hasFatData
                ? [
                    {
                        label: "Fat Hours",
                        data: fatData,
                        borderColor: "#F5B800",
                        backgroundColor: "rgba(245, 184, 0, 0.06)",
                        borderWidth: 2,
                        spanGaps: false,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHitRadius: 14,
                        pointHoverBackgroundColor: "#F5B800",
                        pointHoverBorderColor: "#FFFFFF",
                        pointHoverBorderWidth: 2,
                        tension: 0,
                        fill: false,
                        yAxisID: "y1",
                    },
                ]
                : []),
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: "index",
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.82)",
                titleColor: "#FFFFFF",
                bodyColor: "#FFFFFF",
                borderColor: "#1D95E2",
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: (items) => {
                        var _a, _b;
                        const i = (_b = (_a = items[0]) === null || _a === void 0 ? void 0 : _a.dataIndex) !== null && _b !== void 0 ? _b : 0;
                        const p = processedData[i];
                        if (p === null || p === void 0 ? void 0 : p.dateTimeIso) {
                            try {
                                return new Date(p.dateTimeIso).toLocaleString(undefined, { hour12: false });
                            }
                            catch (_c) {
                                return p.month || "";
                            }
                        }
                        return (p === null || p === void 0 ? void 0 : p.month) || "";
                    },
                    label: (ctx) => {
                        const p = processedData[ctx.dataIndex];
                        if (ctx.datasetIndex === 1) {
                            const val = fatData[ctx.dataIndex];
                            if (val == null)
                                return "";
                            return `Fat hours: ${formatFatDuration(val)}`;
                        }
                        if (!p || p.temperature == null)
                            return "";
                        if (p.isAggregated && p.minTemperature != null && p.maxTemperature != null) {
                            return [
                                `Average: ${p.temperature} °C`,
                                `Min: ${p.minTemperature} °C`,
                                `Max: ${p.maxTemperature} °C`,
                            ];
                        }
                        return `Temperature: ${p.temperature} °C`;
                    },
                },
            },
        },
        scales: Object.assign({ x: {
                display: true,
                grid: { display: false },
                border: { display: false, color: theme.palette.divider },
                ticks: {
                    color: theme.palette.text.secondary,
                    font: { size: isMobile ? 8 : 9 },
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: maxXLabels,
                },
            }, y: {
                display: true,
                min: normalizedYMin,
                max: normalizedYMax,
                grid: { color: theme.palette.action.hover, drawBorder: false },
                border: { display: false },
                ticks: { color: theme.palette.text.secondary, stepSize },
            } }, (hasFatData
            ? {
                y1: {
                    type: "linear",
                    display: true,
                    position: "right",
                    grid: { drawOnChartArea: false },
                    border: { display: false },
                    ticks: {
                        color: "#F5B800",
                        callback: (value) => formatFatDuration(value),
                    },
                },
            }
            : {})),
        layout: {
            padding: {
                left: isMobile ? 4 : 8,
                right: isMobile ? 8 : 12,
                top: isMobile ? 8 : 12,
                bottom: isMobile ? 4 : 8,
            },
        },
    };
    const caption = isAggregatedView
        ? "Time buckets (readings merged for this chart width — tooltip shows min/avg/max per bucket)"
        : "All readings in range (one point per log)";
    return (React.createElement(FCard, null,
        React.createElement(Box, { py: 1.5, px: 1.5 },
            React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: { xs: 1, sm: 1.5 },
                } },
                React.createElement(Typography, { variant: "h6" }, title),
                React.createElement(TemperatureIcon, { size: 24 })),
            React.createElement(Box, { ref: containerRef, sx: {
                    width: "100%",
                    height: { xs: 250, sm: 253 },
                    maxHeight: 300,
                    position: "relative",
                    px: { xs: 0.5, sm: 1 },
                } }, hasTemperatureData ? (React.createElement(Line, { data: chartData, options: options })) : (React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { height: "100%", color: "text.secondary" }) },
                React.createElement(Typography, { variant: "body2" }, "No temperature data in range")))),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { gap: 2, mt: 1, flexWrap: "wrap" }) },
                React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { gap: 1 }) },
                    React.createElement(Box, { sx: {
                            width: isMobile ? 6 : 8,
                            height: isMobile ? 6 : 8,
                            borderRadius: "50%",
                            backgroundColor: "#1D95E2",
                        } }),
                    React.createElement(Typography, { variant: "caption", color: "text.secondary", textAlign: "center" }, caption)),
                hasFatData && (React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { gap: 1 }) },
                    React.createElement(Box, { sx: {
                            width: isMobile ? 6 : 8,
                            height: isMobile ? 6 : 8,
                            borderRadius: "50%",
                            backgroundColor: "#F5B800",
                        } }),
                    React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "Fat hours")))))));
};
